/**
 * Which addresses are cards, and how a card is written into the address of
 * the page it is opened over (Issue 567).
 *
 * A card — a posting, an employer, a position report — has a page of its
 * own, and in the shell it opens over the page they are on instead of in
 * place of it (2026-09-13, user's ruling). It used to be an intercepting
 * route (`@modal/(.)jobs/[key]`). Next 16 cannot redraw one: a refresh under
 * it fetches the page beneath separately, with a Next-Url that intercepts
 * that page too when it is itself a card's address, and after a way back it
 * has lost where that page came from at all — either way the tree it gets
 * does not match and it falls back to a hard load of the card's own page.
 * So the card is now a query parameter of the page beneath,
 * `?card=job:<key>`, drawn by the `@modal` slot like any other page: a
 * refresh, a way back and a way forward are each one ordinary request.
 *
 * This file is the one place that knows which addresses are cards and the
 * parameter; each card's own address is spelled by `lib/links.ts`, which
 * everything that links to one uses. A link keeps the card's own address as
 * its `href` (a new tab, a copied link: the page); the shell's soft
 * navigation turns a press on it into the parameter (`NavigationPending`),
 * and a whole-page load of an address carrying one goes to the card's own
 * page (`proxy.ts`). Pure, and free of the database, because the browser
 * runs it.
 */
import { PAGES, pageHref, type PageKind } from "@/lib/links";

/** What opens as a card over the page. Named here, not taken from every
 *  page lib/links.ts knows: a page is not a card until someone decides it
 *  is one. CardOver draws each kind by name — a kind added here without a
 *  branch there is a type error (#587). */
const KINDS = ["job", "company", "report"] as const satisfies readonly PageKind[];
export type CardKind = (typeof KINDS)[number];
export type Card = Readonly<{ kind: CardKind; id: string }>;

/** The query parameter a card is carried in. */
export const CARD_PARAM = "card";

function decoded(segment: string): string | null {
  try {
    return decodeURIComponent(segment);
  } catch {
    return null;
  }
}

/** The card whose own page this path is — `/jobs/<key>`, `/companies/<slug>`,
 *  `/reports/<id>`, one segment each — or null. */
export function cardAt(pathname: string): Card | null {
  const parts = pathname.split("/");
  // "", the kind's segment, the id — and nothing after it but a trailing slash.
  if (parts.length < 3 || parts.length > 4 || parts[0] !== "" || (parts.length === 4 && parts[3] !== "")) return null;
  const kind = KINDS.find((k) => PAGES[k] === parts[1]);
  const id = parts[2] ? decoded(parts[2]) : null;
  return kind && id ? { kind, id } : null;
}

/** The parameter's value for a card: `<kind>:<id>`. */
export function cardParam(card: Card): string {
  return `${card.kind}:${card.id}`;
}

/** The card a query string carries, or null — a value this file did not
 *  write is no card at all. */
export function readCard(search: URLSearchParams | string | null | undefined): Card | null {
  const params = typeof search === "string" || !search ? new URLSearchParams(search ?? "") : search;
  const value = params.get(CARD_PARAM);
  if (!value) return null;
  const at = value.indexOf(":");
  const kind = KINDS.find((k) => k === value.slice(0, at));
  const id = value.slice(at + 1);
  return at > 0 && kind && id ? { kind, id } : null;
}

/** The address of `here` with that card over it (one card: it replaces any
 *  other). Path and query only — a card is not a place on the page. */
export function overHref(here: URL | string, card: Card): string {
  const url = new URL(here, "http://x");
  url.searchParams.set(CARD_PARAM, cardParam(card));
  return url.pathname + url.search;
}

/** How many steps back the page beneath the card is: this page with no card
 *  over it, past every card opened on it since — a card opened from another
 *  card (a company's openings) is a second entry over the same page. `urls`
 *  is the session history (the Navigation API's entries, null where the
 *  browser will not say) and `current` this entry's index in it.
 *
 *  1 — an ordinary back — when there is no such entry to find: no card here,
 *  another page in between, or a history that does not reach back that far. */
export function stepsUnder(urls: readonly (string | null)[], current: number): number {
  const at = urls[current];
  if (at == null) return 1;
  const here = new URL(at, "http://x");
  if (!readCard(here.searchParams)) return 1;
  for (let i = current - 1; i >= 0; i--) {
    const raw = urls[i];
    if (raw == null) return 1;
    const u = new URL(raw, "http://x");
    if (u.pathname !== here.pathname) return 1;
    if (!readCard(u.searchParams)) return current - i;
  }
  return 1;
}

/** The card's own page (`lib/links.ts`, the one spelling of it). */
export function ownHref(card: Card): string {
  return pageHref(card.kind, card.id);
}

/**
 * Where a whole-page load of `url` goes when it carries a card: the card's
 * own page (`proxy.ts cardLanding`). An opening's page is given the way back
 * the page beneath had — `?from=` (jobs/query.ts `deepBack`): 面试 under the
 * interviews page, the one its links carried when the card's address was the
 * address bar's. Under 岗位 the way back is the ledger, which is its default
 * (岗位 · 今日, the other one, went with #640).
 */
export function landingOf(url: URL): string | null {
  const card = readCard(url.searchParams);
  if (!card) return null;
  const own = ownHref(card);
  if (card.kind !== "job") return own;
  const path = url.pathname.replace(/\/+$/, "");
  return path === "/interviews" ? `${own}?from=interviews` : own;
}
